import { Link } from "react-router-dom";

interface Solution {
  icon: string;
  title: string;
  problem: string;
  solution: string;
  products: string[];
  outcome: string;
}

const PRODUCT_IDS: Record<string, string> = {
  HCM: "hris",
  WorkTrack: "worktrack",
  PMS: "pms",
  OMS: "oms",
  "Quality Audit": "quality-audit",
  "RemoteOps Hub": "remoteops-hub",
  "Growth & Marketing": "growth-marketing",
  "Sales & CRM": "sales-crm",
  "Workplace Services": "workplace-services",
  "Get Fit": "get-fit",
  Accounting: "finance",
  Admin: "admin",
};

const solutions: Solution[] = [
  {
    icon: "ri-user-line",
    title: "Workforce Management",
    problem: "Managing staff, attendance, and productivity is often fragmented and manual.",
    solution: "StackMap connects HR, tasks, and performance into one system.",
    products: ["HCM", "WorkTrack", "PMS"],
    outcome: "Improved staff coordination, accountability, and visibility.",
  },
  {
    icon: "ri-archive-line",
    title: "Order & Inventory Management",
    problem: "Logistics companies, 3PL operators, and warehouses struggle with fragmented order processing, disconnected inventory data, and lack of client visibility into their own stock and shipments.",
    solution: "OMS is a standalone order and inventory management platform that tracks every SKU from receiving through dispatch, manages inbound and outbound workflows, coordinates multi-warehouse operations, and gives merchants a self-service portal — all with full audit traceability.",
    products: ["OMS", "Quality Audit", "Accounting"],
    outcome: "Complete operational visibility across every warehouse, real-time inventory accuracy, reduced client inquiry volume with self-service portal, and full digital audit trails.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Quality & Operational Compliance",
    problem: "Quality inspections, compliance audits, and operational oversight are paper-based and reactive, leaving businesses exposed to findings, non-conformances, and operational drift.",
    solution: "StackMap combines structured audit management with remote operational oversight, ensuring quality standards are consistently met across every site and every workflow.",
    products: ["Quality Audit", "RemoteOps Hub", "WorkTrack"],
    outcome: "Proactive quality assurance, faster audit cycles, complete compliance documentation, and real-time operational visibility across every location.",
  },
  {
    icon: "ri-bar-chart-line",
    title: "Performance & Accountability",
    problem: "Lack of clear KPIs and performance tracking reduces productivity.",
    solution: "StackMap enables structured goal setting and performance monitoring.",
    products: ["PMS", "WorkTrack"],
    outcome: "Clear accountability and improved team performance.",
  },
  {
    icon: "ri-remote-control-line",
    title: "Remote Operations Management",
    problem: "Managing multiple locations or teams remotely is difficult and inefficient.",
    solution: "StackMap centralizes control and provides real-time operational visibility.",
    products: ["RemoteOps Hub", "WorkTrack", "PMS"],
    outcome: "Full control across locations with real-time insights.",
  },
  {
    icon: "ri-megaphone-line",
    title: "Customer Acquisition & Pipeline",
    problem: "Leads go cold between marketing and sales because teams work in disconnected tools with no handoff process.",
    solution: "StackMap connects campaign execution, lead capture, and sales pipeline management in one seamless flow.",
    products: ["Growth & Marketing", "Sales & CRM"],
    outcome: "Higher conversion rates, zero lead leakage, and complete visibility from first touch to closed deal.",
  },
  {
    icon: "ri-building-4-line",
    title: "Workplace Operations & Employee Experience",
    problem: "Internal service requests get lost in email chains and hallway conversations, frustrating employees and overwhelming facilities teams.",
    solution: "StackMap digitizes every workplace service request with structured workflows, approvals, and real-time SLA tracking.",
    products: ["Workplace Services", "Admin", "HCM"],
    outcome: "Faster service resolution, higher employee satisfaction, and complete operational visibility for workplace managers.",
  },
  {
    icon: "ri-heart-pulse-line",
    title: "Gym & Wellness Management",
    problem: "Gyms and wellness businesses juggle memberships, class schedules, trainer coordination, and payments across disconnected spreadsheets and apps, hurting member retention and operational efficiency.",
    solution: "Get Fit brings member management, class booking, trainer tools, progress tracking, and billing into one unified fitness platform, supported by HCM for staff management and Growth & Marketing for member acquisition.",
    products: ["Get Fit", "HCM", "Growth & Marketing", "Accounting"],
    outcome: "Higher member retention, streamlined gym operations, data-driven growth insights, and a connected fitness community experience.",
  },
];

import { useDemoRequest } from "@/components/feature/DemoRequest";

export default function SolutionCards() {
  const { open } = useDemoRequest();
  return (
    <section className="py-10 md:py-14">
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {solutions.map((s) => (
              <div
                key={s.title}
                className="glass-card p-5 sm:p-6 md:p-7 flex flex-col h-full group"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-graphite-800 group-hover:bg-emerald-accent/10 transition-colors mb-5">
                  <i className={`${s.icon} text-2xl text-emerald-accent`} />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-white mb-4 group-hover:text-emerald-soft transition-colors duration-300">
                  {s.title}
                </h3>
                <div className="space-y-4 flex-1">
                  <div>
                    <h4 className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-1.5">
                      Problem
                    </h4>
                    <p className="text-sm text-silver-300 leading-relaxed">
                      {s.problem}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-emerald-soft uppercase tracking-wider mb-1.5">
                      Solution
                    </h4>
                    <p className="text-sm text-silver-300 leading-relaxed">
                      {s.solution}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-silver-300 uppercase tracking-wider mb-2">
                      Products Used
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {s.products.map((p) => (
                        <Link
                          key={p}
                          to={`/products/${PRODUCT_IDS[p] || p.toLowerCase().replace(/\s+/g, '-')}`}
                          className="inline-flex items-center px-2.5 py-1 rounded-md bg-graphite-800 border border-graphite-700 text-xs text-silver-300 hover:text-emerald-soft hover:border-emerald-accent/30 transition-colors"
                        >
                          {p}
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-amber-warning uppercase tracking-wider mb-1.5">
                      Outcome
                    </h4>
                    <p className="text-sm text-silver-300 leading-relaxed">
                      {s.outcome}
                    </p>
                  </div>
                </div>
                <button onClick={open} className="btn-primary text-center text-sm mt-6 whitespace-nowrap cursor-pointer">
                  Request a Demo
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}